import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupabase } from '../../lib/SupabaseProvider';
import { useOrg } from '../../lib/OrgProvider';
import { useOrgMembersIndex } from '../../lib/useOrgMembersIndex';
import { captureException, captureEvent } from '../../lib/telemetry/posthog/frontend';
import { buildConversationCreationProps } from '../../lib/telemetry/conversation-creation-contract';
import { buildOnboardingProps } from '../../lib/telemetry/onboarding-contract';
import { listInterviewSessions, listInterviewSessionsByOrg, listInterviewSessionsByWorkspace, deleteInterviewSession, closeSession, reopenSession, getResponseSummariesForSessions } from '../../services/interviewService';
import { getSupabase } from '../../lib/supabase';
import { buildMoveToWorkspaceItems, moveSessionToWorkspace, WorkspaceMoveError } from '../workspace/moveToWorkspace';
import { IconFolder, IconTrash, IconCopy, IconPencil, IconBan, IconDotsVertical, IconSearch } from '@tabler/icons-react';
import { PlayIcon, PlusIcon, CloseIcon } from '@/components/icons';
import type { InterviewSession } from '../../types';
import { getCampaignDisplayStatus, sessionStatusLabels, formatDate } from '../../lib/admin-utils';
import type { CampaignDisplayStatus } from '../../lib/admin-utils';
import { ConfirmDialog, DropdownMenu, Skeleton } from '../ui';
import { Tooltip } from '../Tooltip';
import { buildCloneNavigationState } from '../../lib/clone-session';
import { clearSetupDraft } from '../setupDraft';
import { useAdminLayout } from './AdminLayout';
import { CollectionPresence } from '../ui/CollectionPresence';
import './AdminDashboard.css';

import { AnimatedLabel } from '../ui/AnimatedLabel';
import { FilterChipRow } from '../ui/FilterChipRow';
import { matchesCampaignSearch } from '../../lib/campaign-search';
import { clsx } from 'clsx';

/**
 * Main dashboard view showing all campaigns with response counts and status filters.
 * Loads response counts in parallel for all sessions on mount.
 */
export const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSupabase();
  const { activeOrg, hasPermission, workspaces, activeWorkspace } = useOrg();
  // Workspace label. Workspace is the primary scope; fall back
  // to the org name (and finally a generic "Dashboard") so the title is
  // never blank during the brief window before the workspace resolves.
  const dashboardLabel = activeWorkspace?.name ?? activeOrg?.name ?? 'Dashboard';
  /** Permission-gated CTA - see OrgProvider.hasPermission. Sourced from the
   *  `org_role_permissions` junction, not a hardcoded role-name list. */
  const canCreateInterview = hasPermission('interviews:create');
  /** Editing, closing, reopening and cloning require campaign edit permission. */
  const canEditInterview = hasPermission('interviews:edit');
  /** Destructive - gated separately so editors can edit but not delete. */
  const canDeleteInterview = hasPermission('interviews:delete');
  const { refreshSidebar } = useAdminLayout();
  const membersIndex = useOrgMembersIndex(activeOrg?.id ?? null);
  const [sessions, setSessions] = useState<InterviewSession[]>([]);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [{ filter, query: searchQuery }, setCriteria] = useState<{ filter: CampaignDisplayStatus | 'all'; query: string }>({ filter: 'all', query: '' });
  const [deleteConfirmSession, setDeleteConfirmSession] = useState<InterviewSession | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [responseCounts, setResponseCounts] = useState<Record<string, number>>({});
  const [respondentNames, setRespondentNames] = useState<Record<string, string[]>>({});
  const [closeConfirmSession, setCloseConfirmSession] = useState<InterviewSession | null>(null);
  const [closing, setClosing] = useState(false);
  const [reopenConfirmSession, setReopenConfirmSession] = useState<InterviewSession | null>(null);
  const [reopening, setReopening] = useState(false);
  const searchInputRef = useRef<HTMLInputElement>(null);

  /** Apply both restrictions together so every visible action uses current results. */
  const updateCollectionCriteria = (nextFilter: CampaignDisplayStatus | 'all', query: string) => {
    setCriteria({ filter: nextFilter, query });
  };
  // Stale-while-revalidate flag: tracks whether we have ever successfully
  // completed a sessions fetch. On cold load (false) the content skeleton
  // reserves space while the platform navigation stays available.
  // On warm refetches (true), e.g. when flipping workspace via the rail
  // switcher, we keep the previous list rendered and quietly swap in the
  // new data so there is no loading flash. Mirrors the pattern used in
  // InterviewDetailView.loadData (see comment there for rationale).
  const hasLoadedOnceRef = useRef(false);
  // Latest-wins guard (A11): each load stamps this with a monotonic id; a slow
  // response that lands after the user flipped org/workspace is discarded so
  // the dashboard never shows the previous scope's data.
  const loadEpochRef = useRef(0);

  useEffect(() => {
    if (user?.id) {
      void loadSessions();
    }
    // A pending empty result must not navigate after the user leaves this page.
    return () => {
      loadEpochRef.current += 1;
    };
    // activeOrg?.id / activeWorkspace?.id: refetch when the user flips orgs
    // or workspaces via the rail switchers, else the dashboard lingers on
    // the previous scope.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, activeOrg?.id, activeWorkspace?.id]);

  const loadSessions = async () => {
    if (!user?.id) return;

    // Stamp this load so a stale response (resolved after an org/workspace
    // switch) can be discarded instead of overwriting the current scope.
    const epoch = ++loadEpochRef.current;

    // Stale-while-revalidate: only block the UI on a true cold load. On
    // subsequent workspace / org switches keep the previous list visible
    // until the new data arrives so the page does not flash a full-screen
    // loader for what is usually a sub-200ms warm fetch.
    if (!hasLoadedOnceRef.current) setLoading(true);
    setError(null);

    try {
      // Scope: workspace (if active) → org → user. Workspaces are the new
      // primary scope per the Phase A workspaces feature; the org and user
      // fallbacks remain for legacy data and the moments before workspaces
      // resolve.
      const data = activeOrg?.id && activeWorkspace?.id
        ? await listInterviewSessionsByWorkspace(activeOrg.id, activeWorkspace.id)
        : activeOrg?.id
          ? await listInterviewSessionsByOrg(activeOrg.id)
          : await listInterviewSessions(user.id);

      // Drop a stale list: the scope changed while this was in flight.
      if (epoch !== loadEpochRef.current) return;
      setSessions(data);

      // Load response counts + respondent names for ALL sessions in ONE
      // aggregate query (replaces the per-session N+1, A11).
      const { counts, names } = await getResponseSummariesForSessions(
        data.map((s) => s.id),
      );

      if (epoch !== loadEpochRef.current) return;
      setResponseCounts(counts);
      setRespondentNames(names);

      // Reuse the existing builder for an empty workspace. Automatic entry
      // preserves any in-progress draft and replaces the empty history entry.
      if (data.length === 0 && canCreateInterview) {
        captureEvent('conversation_creation.started', buildConversationCreationProps(activeWorkspace?.id, {
          source: 'dashboard',
          automatic: true,
        }));
        void navigate('/admin/setup', { replace: true });
      }
    } catch (err) {
      if (epoch !== loadEpochRef.current) return;
      captureException(err instanceof Error ? err : String(err), 'AdminDashboard.loadSessions', { userId: user?.id });
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      if (epoch === loadEpochRef.current) {
        setLoading(false);
        hasLoadedOnceRef.current = true;
      }
    }
  };

  const handleCreateNew = () => {
    // Start fresh: drop any in-session builder draft so a prior unfinished
    // build isn't restored onto the new blank wizard.
    clearSetupDraft();
    captureEvent('conversation_creation.started', buildConversationCreationProps(activeWorkspace?.id, { source: 'dashboard' }));
    if (sessions.length === 0) {
      captureEvent('onboarding.first_create_clicked', buildOnboardingProps());
    }
    void navigate('/admin/setup');
  };

  const handleViewDetails = (sessionId: string) => {
    void navigate(`/admin/interviews/${sessionId}`);
  };

  const handleDeleteClick = (e: React.MouseEvent, session: InterviewSession) => {
    e.stopPropagation();
    setDeleteConfirmSession(session);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmSession) return;

    setDeleting(true);
    try {
      await deleteInterviewSession(deleteConfirmSession.id);
      setSessions(sessions.filter(s => s.id !== deleteConfirmSession.id));
      setDeleteConfirmSession(null);
      refreshSidebar();
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'AdminDashboard.deleteInterview', { sessionId: deleteConfirmSession?.id });
      setError(err instanceof Error ? err.message : 'Failed to delete');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmSession(null);
  };

  const handleCloseClick = (e: React.MouseEvent, session: InterviewSession) => {
    e.stopPropagation();
    setCloseConfirmSession(session);
  };

  const handleCloseConfirm = async () => {
    if (!closeConfirmSession) return;

    setClosing(true);
    try {
      await closeSession(closeConfirmSession.id);
      setSessions(sessions.map(s =>
        s.id === closeConfirmSession.id ? { ...s, status: 'completed' as const } : s
      ));
      setCloseConfirmSession(null);
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'AdminDashboard.closeSession', { sessionId: closeConfirmSession.id });
      setError(err instanceof Error ? err.message : 'Failed to close campaign');
    } finally {
      setClosing(false);
    }
  };

  const handleCloseCancel = () => {
    setCloseConfirmSession(null);
  };

  const handleReopenClick = (e: React.MouseEvent, session: InterviewSession) => {
    e.stopPropagation();
    setReopenConfirmSession(session);
  };

  const handleReopenConfirm = async () => {
    if (!reopenConfirmSession) return;

    setReopening(true);
    try {
      await reopenSession(reopenConfirmSession.id);
      setSessions(sessions.map(s =>
        s.id === reopenConfirmSession.id ? {
          ...s,
          status: 'active' as const,
          closes_at: null,
          ...(s.opens_at && new Date(s.opens_at) > new Date() ? { opens_at: null } : {}),
        } : s
      ));
      setReopenConfirmSession(null);
    } catch (err) {
      captureException(err instanceof Error ? err : String(err), 'AdminDashboard.reopenSession', { sessionId: reopenConfirmSession.id });
      setError(err instanceof Error ? err.message : 'Failed to re-open campaign');
    } finally {
      setReopening(false);
    }
  };

  const handleReopenCancel = () => {
    setReopenConfirmSession(null);
  };

  /** Open the campaign's Questions tab. */
  const handleEditClick = (e: React.MouseEvent, sessionId: string) => {
    e.stopPropagation();
    void navigate(`/admin/interviews/${sessionId}/edit`);
  };

  const handleCloneClick = (e: React.MouseEvent, session: InterviewSession) => {
    e.stopPropagation();
    captureEvent('conversation_creation.started', buildConversationCreationProps(activeWorkspace?.id, { source: 'clone', source_session_id: session.id }));
    void navigate('/admin/setup', { state: buildCloneNavigationState(session) });
  };

  /**
   * Move a campaign to a different workspace via `moveSessionToWorkspace`.
   * Optimistic update removes the row from the current dashboard list (since
   * the dashboard is workspace-scoped, the campaign disappears from view on
   * success). On failure we rollback by reloading the list and surface a
   * non-blocking error banner. PostHog capture covers the error path so we
   * see if RLS or column drift starts denying moves in production.
   */
  const handleMoveToWorkspace = async (
    e: React.MouseEvent,
    session: InterviewSession,
    targetWorkspaceId: string,
  ) => {
    e.stopPropagation();
    setMoveError(null);
    const previous = sessions;
    setSessions((prev) => prev.filter((s) => s.id !== session.id));
    try {
      await moveSessionToWorkspace(getSupabase(), session.id, targetWorkspaceId);
      refreshSidebar();
    } catch (err) {
      if (!(err instanceof WorkspaceMoveError && err.code === 'workspace_asset_mismatch')) captureException(
        err instanceof Error ? err : new Error(String(err)),
        'AdminDashboard.moveToWorkspace',
        {
          sessionId: session.id,
          fromWorkspaceId: activeWorkspace?.id,
          toWorkspaceId: targetWorkspaceId,
        },
      );
      // Rollback the optimistic update so the list reflects reality.
      setSessions(previous);
      setMoveError(err instanceof WorkspaceMoveError ? err.message : 'Could not move this.');
    }
  };

  const searchMatches = sessions.filter(session => matchesCampaignSearch(searchQuery, [
    session.target_name, session.goal ?? '', ...(respondentNames[session.id] ?? []),
  ]));
  const filteredSessions = searchMatches.filter(session => filter === 'all'
    || getCampaignDisplayStatus(session) === filter);
  const visibleSessions: Array<InterviewSession | null> = loading ? Array.from({ length: 6 }, () => null) : filteredSessions;
  const collectionKey = JSON.stringify([loading, Boolean(error), filteredSessions.map(session => session.id)]);
  const statusOptions = (['active', 'scheduled', 'completed'] as const).map(value => ({
    value,
    label: sessionStatusLabels[value],
  }));
  // Keep identifying context stable even when search leaves just one duplicate visible.
  const titleCounts = new Map<string, number>();
  sessions.forEach(session => {
    const title = session.target_name.trim().toLocaleLowerCase();
    titleCounts.set(title, (titleCounts.get(title) ?? 0) + 1);
  });

  /** Reset both restrictions and return the user to the input they can refine. */
  const clearFilters = () => {
    updateCollectionCriteria('all', '');
    searchInputRef.current?.focus();
  };

  return (
    <div className="dashboard-shell pt-16 md:pt-0">
      {/* Sticky header bar - matches CampaignHeader style */}
      <div className="border-b border-border bg-background/95 backdrop-blur-sm sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center gap-4">
            {/* Keep the workspace title aligned with the primary action. */}
            <div className="min-w-0">
              <div className="flex items-center min-h-[36px]">
                <h1 className="text-xl font-bold text-foreground truncate">{dashboardLabel}</h1>
              </div>
            </div>
            {/* Wrapper owns responsive + alignment so the Tooltip doesn't
                swallow or break those classes when the viewer state adds its
                `<div className="relative">` shell. */}
            <div className="ml-auto shrink-0">
              <Tooltip
                content={canCreateInterview ? '' : 'You have read-only access'}
                position="bottom"
              >
                <button
                  onClick={handleCreateNew}
                  disabled={!canCreateInterview}
                  aria-disabled={!canCreateInterview || undefined}
                  className={clsx(
                    'flex min-h-10 items-center gap-2 px-4 py-2 button-accent font-medium text-sm rounded-lg cursor-pointer transition-colors',
                    canCreateInterview ? 'hover:opacity-90' : 'opacity-50 cursor-not-allowed',
                  )}
                >
                  <PlusIcon className="w-4 h-4" />
                  Create
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        <div className="dashboard-collection-toolbar">
          <div className="dashboard-search">
            <IconSearch className="h-4 w-4 shrink-0" aria-hidden="true" />
            <input ref={searchInputRef} type="search" value={searchQuery} onChange={(e) => updateCollectionCriteria(filter, e.target.value)}
              placeholder="Search..." aria-label="Search" />
            <button type="button" onClick={() => { updateCollectionCriteria(filter, ''); searchInputRef.current?.focus(); }} aria-label="Clear search"
              disabled={!searchQuery} aria-hidden={!searchQuery} tabIndex={searchQuery ? 0 : -1}
              className={clsx('dashboard-clear-search', !searchQuery && 'opacity-0 pointer-events-none')}>
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>
          <div className="dashboard-status-filters">
            <FilterChipRow ariaLabel="Filter campaigns" options={statusOptions} selected={filter === 'all' ? null : filter}
              onSelect={value => updateCollectionCriteria((value as CampaignDisplayStatus | null) ?? 'all', searchQuery)} />
          </div>
          <span className="dashboard-results-count" role="status" aria-label="Campaign results" aria-live="polite">
            <AnimatedLabel text={loading ? '' : `${filteredSessions.length} ${filteredSessions.length === 1 ? 'campaign' : 'campaigns'}`} />
          </span>
        </div>

        <div role="status" aria-label="Workspace move" aria-live="polite">
          {moveError && <div className="mb-4 flex items-center gap-3 rounded-lg bg-wash-danger px-4 py-2 text-sm text-danger-soft">
            <p className="min-w-0 flex-1">{moveError}</p>
            <button type="button" aria-label="Dismiss move message" onClick={() => setMoveError(null)}
              className="flex min-h-11 min-w-11 shrink-0 cursor-pointer items-center justify-center rounded-lg hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-volt">
              <CloseIcon className="h-4 w-4" />
            </button>
          </div>}
        </div>

        <CollectionPresence className="dashboard-results" contentKey={collectionKey}>
        {error ? (
          <div className="bg-wash-danger rounded-lg p-6 text-center">
            <p className="text-danger-soft">{error}</p>
            <button
              onClick={loadSessions}
              className="mt-4 text-sm text-volt-soft hover:text-volt-soft"
            >
              Try again
            </button>
          </div>
        ) : !loading && filteredSessions.length === 0 && sessions.length === 0 ? (
          <p className="py-6 text-sm text-muted-foreground">Nothing here yet.</p>
        ) : !loading && filteredSessions.length === 0 ? (
          <div className="dashboard-empty-results">
            <h2>No matches</h2>
            <button type="button" onClick={clearFilters} className="dashboard-reset-button">Clear filters</button>
          </div>
        ) : (
          <div className="dashboard-campaign-grid" role={loading ? 'status' : undefined} aria-label={loading ? 'Loading campaigns' : undefined} aria-busy={loading || undefined}>
            {visibleSessions.map((session, index) => (
              <article
                key={session?.id ?? `loading-${index}`}
                aria-hidden={!session || undefined}
                className="dashboard-campaign"
              >
                <div className="dashboard-campaign-heading">
                  <Tooltip content={(() => {
                    if (!session) return '';
                    const creator = membersIndex[session.created_by];
                    const name = creator?.name || creator?.email;
                    return `${session.target_name}${session.goal ? ` · ${session.goal}` : ''}\n${name ? `Created by ${name} · ` : ''}${formatDate(session.created_at)}`;
                  })()} position="top">
                    <button type="button" aria-label={session?.target_name} disabled={!session} onClick={() => session && handleViewDetails(session.id)} className="dashboard-campaign-open">
                      <span role="heading" aria-level={3} className="dashboard-campaign-title">{session?.target_name ?? <Skeleton inline width="72%" />}</span>
                    </button>
                  </Tooltip>
                  <div className="dashboard-campaign-metadata">
                    <span className="dashboard-campaign-status">{session ? sessionStatusLabels[getCampaignDisplayStatus(session)] : <Skeleton inline width="5ch" />}</span>
                    {session && (titleCounts.get(session.target_name.trim().toLocaleLowerCase()) ?? 0) > 1 && (
                      <time className="dashboard-campaign-date" dateTime={session.created_at}
                        aria-label={`Created ${formatDate(session.created_at)}`}>{formatDate(session.created_at)}</time>
                    )}
                  </div>
                </div>
                <div className="dashboard-campaign-activity">
                  <button type="button" disabled={!session} onClick={() => { if (session) void navigate(`/admin/interviews/${session.id}?tab=responses`); }}
                    aria-label={session ? `${responseCounts[session.id] ?? 0} ${responseCounts[session.id] === 1 ? 'response' : 'responses'} for ${session.target_name}` : undefined}
                    className="dashboard-response-link">
                    {session ? <span aria-label={`${responseCounts[session.id] ?? 0}${session.max_responses ? ` / ${session.max_responses}` : ''} ${responseCounts[session.id] === 1 && !session.max_responses ? 'response' : 'responses'}`}>
                      <span className="dashboard-response-count">{responseCounts[session.id] ?? 0}</span>
                      {session.max_responses ? <span className="dashboard-response-limit"> / {session.max_responses}</span> : null}
                      <span className="dashboard-response-label"> {responseCounts[session.id] === 1 && !session.max_responses ? 'response' : 'responses'}</span>
                    </span> : <span className="dashboard-response-count"><Skeleton inline width="9ch" /></span>}
                  </button>
                  {session?.max_responses ? (
                    <div className="dashboard-response-progress" role="progressbar" aria-label="Responses collected"
                      aria-valuemin={0} aria-valuemax={session.max_responses} aria-valuenow={Math.min(responseCounts[session.id] ?? 0, session.max_responses)}>
                      <span style={{ transform: `scaleX(${Math.min(1, (responseCounts[session.id] ?? 0) / session.max_responses)})` }} />
                    </div>
                  ) : null}
                </div>
                <div className="dashboard-campaign-actions">
                    {(() => {
                      if (!session) return canEditInterview || canDeleteInterview
                        ? <button type="button" disabled tabIndex={-1} className="dashboard-menu-button"><IconDotsVertical className="w-4 h-4" /></button>
                        : null;
                      // Build items first so we can short-circuit the whole
                      // trigger when the user has no actions available -
                      // an empty menu feels broken (user opens it, sees
                      // nothing, closes it).
                      const status = getCampaignDisplayStatus(session);
                      const items = [
                        ...(canEditInterview ? [{
                          label: 'Edit',
                          icon: <IconPencil className="w-4 h-4" />,
                          onClick: (e: React.MouseEvent) => handleEditClick(e, session.id),
                          className: 'text-foreground',
                        }] : []),
                        // Clone creates a new session, so also requires create.
                        ...(canEditInterview && canCreateInterview ? [{
                          label: 'Duplicate',
                          icon: <IconCopy className="w-4 h-4" />,
                          onClick: (e: React.MouseEvent) => handleCloneClick(e, session),
                          className: 'text-foreground',
                        }] : []),
                        // One heading keeps destination names easy to scan while
                        // preserving direct selection and permission gates.
                        ...(canEditInterview && activeWorkspace
                          ? buildMoveToWorkspaceItems(activeWorkspace.id, workspaces).map((target, index) => ({
                              label: target.label,
                              section: index === 0 ? 'Move to' : undefined,
                              icon: <IconFolder className="w-4 h-4" />,
                              onClick: (e: React.MouseEvent) =>
                                handleMoveToWorkspace(e, session, target.id),
                              className: 'text-foreground',
                            }))
                          : []),
                        ...(canEditInterview && (status === 'active' || status === 'scheduled') ? [{
                          label: 'Close campaign',
                          separatorBefore: true,
                          icon: <IconBan className="w-4 h-4" />,
                          onClick: (e: React.MouseEvent) => handleCloseClick(e, session),
                          className: 'text-foreground',
                        }] : []),
                        ...(canEditInterview && status === 'completed' ? [{
                          label: 'Reopen campaign',
                          separatorBefore: true,
                          icon: <PlayIcon className="w-4 h-4" />,
                          onClick: (e: React.MouseEvent) => handleReopenClick(e, session),
                          className: 'text-foreground',
                        }] : []),
                        ...(canDeleteInterview ? [{
                          label: 'Delete',
                          icon: <IconTrash className="w-4 h-4" />,
                          onClick: (e: React.MouseEvent) => handleDeleteClick(e, session),
                          className: 'text-danger-soft',
                        }] : []),
                      ];
                      if (items.length === 0) return null;
                      return (
                        <DropdownMenu
                          appearance="compact"
                          anchor="bottom end"
                          width="w-52"
                          trigger={
                            <button
                              onClick={(e) => e.stopPropagation()}
                              className="dashboard-menu-button"
                              aria-label={`More actions for ${session.target_name}`}
                            >
                              <IconDotsVertical className="w-4 h-4" />
                            </button>
                          }
                          items={items}
                        />
                      );
                    })()}
                </div>
              </article>
            ))}
          </div>
        )}
        </CollectionPresence>
      </div>

      <ConfirmDialog
        open={!!deleteConfirmSession}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete?"
        description={<>Are you sure you want to delete <span className="font-semibold text-foreground">{deleteConfirmSession?.target_name}</span>? This action cannot be undone and will permanently delete all <span className="font-semibold text-foreground">{responseCounts[deleteConfirmSession?.id || ''] ?? 0} {responseCounts[deleteConfirmSession?.id || ''] === 1 ? 'response' : 'responses'}</span>.</>}
        confirmLabel="Delete"
        loading={deleting}
        variant="danger"
      />

      <ConfirmDialog
        open={!!closeConfirmSession}
        onClose={handleCloseCancel}
        onConfirm={handleCloseConfirm}
        title="Close?"
        description={<>Are you sure you want to close <span className="font-semibold text-foreground">{closeConfirmSession?.target_name}</span>? This will prevent new responses from being submitted. Existing responses will not be affected.</>}
        confirmLabel="Close"
        loading={closing}
        variant="warning"
      />

      <ConfirmDialog
        open={!!reopenConfirmSession}
        onClose={handleReopenCancel}
        onConfirm={handleReopenConfirm}
        title="Reopen?"
        description={<>Are you sure you want to reopen <span className="font-semibold text-foreground">{reopenConfirmSession?.target_name}</span>? This will allow new responses to be submitted again.</>}
        confirmLabel="Reopen"
        loading={reopening}
        variant="warning"
      />
    </div>
  );
};

export default AdminDashboard;
